import { httpClient } from "./client";
import type { FacilityApi } from "./interfaces";
import type {
  PublicFacilityResponse,
  PublicFacilitySearchResponse,
} from "../types/generated";
import type {
  FacilityCreate,
  FacilityUpdate,
  FacilityResponse,
  FacilityPlaceSubmit,
  FacilityPlaceSubmissionResponse,
  FacilityPlaceClaimResponse,
  FacilityPlaceResponse,
  FacilityOperatingHoursChangeCommand,
  FacilityOperatingHoursChangePreview,
  FacilityOperatingHoursDateProjection,
  FacilityOperatingHoursProjectionPage,
  FacilityOperatingHoursSnapshot,
  CourtCreate,
  CourtUpdate,
  CourtResponse,
  PricingRuleCreate,
  PricingRuleUpdate,
  PricingRuleResponse,
  ReservationQuoteRequest,
  ReservationQuoteResponse,
  CartQuoteRequest,
  CartQuoteResponse,
  BatchBookingRequest,
  BatchBookingResponse,
  MembershipPlanCreate,
  MembershipPlanUpdate,
  MembershipPlanResponse,
  MembershipPriceOverrideCreate,
  MembershipPriceOverrideResponse,
  SessionCreate,
  SessionResponse,
  CheckInResponse,
  CancellationPolicyCreate,
  CancellationPolicyResponse,
  CourtDeactivationResponse,
  BookingCreate,
  BookingResponse,
  AvailabilitySlot,
  FamilyGroupCreate,
  FamilyGroupMemberResponse,
  FamilyGroupResponse,
  UUID,
} from "../types";
import type {
  FacilityCampaignPublicResponse,
  OpenPlayFacility,
  SkillAssessmentCreate,
  PlayerRatingResponse,
  BookingParticipantResponse,
  BookingParticipantAdd,
  BookingInvitationResponse,
  BookingCancelRequest,
  BookingCancellationQuoteResponse,
  BookingCancellationResponse,
} from "../types/generated";

export const facilityApi: FacilityApi = {
  search: (params) => {
    const sp = new URLSearchParams();
    if (params?.city) sp.set("city", params.city);
    if (params?.service) sp.set("service", params.service);
    if (params?.name) sp.set("name", params.name);
    if (params?.query) sp.set("query", params.query);
    // Optional location center → Haversine distance_km + nearest-first sort
    // (facility-discovery B1). lat/lng must travel together to be meaningful.
    if (params?.latitude !== undefined)
      sp.set("latitude", String(params.latitude));
    if (params?.longitude !== undefined)
      sp.set("longitude", String(params.longitude));
    if (params?.radius_km !== undefined)
      sp.set("radius_km", String(params.radius_km));
    if (params?.open_now) sp.set("open_now", "true");
    if (params?.special_offers) sp.set("special_offers", "true");
    if (params?.min_courts !== undefined)
      sp.set("min_courts", String(params.min_courts));
    if (params?.limit !== undefined) sp.set("limit", String(params.limit));
    if (params?.offset !== undefined) sp.set("offset", String(params.offset));
    const qs = sp.toString();
    return httpClient.get<PublicFacilitySearchResponse>(
      `/facilities/search${qs ? `?${qs}` : ""}`,
    );
  },

  create: (data: FacilityCreate) =>
    httpClient.post<FacilityResponse>("/facilities", data),

  submitPlace: (data: FacilityPlaceSubmit) =>
    httpClient.post<FacilityPlaceSubmissionResponse>(
      "/facilities/places",
      data,
    ),

  getPublicPlace: (placeId: UUID) =>
    httpClient.get<FacilityPlaceResponse>(
      `/facilities/places/${encodeURIComponent(placeId)}`,
    ),

  claimPlace: (placeId: UUID, organizationId: UUID, evidence: string) =>
    httpClient.post<FacilityPlaceClaimResponse>(
      `/facilities/places/${encodeURIComponent(placeId)}/claims`,
      { organization_id: organizationId, evidence },
    ),

  getPublic: (facilityId: UUID) =>
    httpClient.get<PublicFacilityResponse>(`/facilities/${facilityId}/public`),

  get: (facilityId: UUID) =>
    httpClient.get<FacilityResponse>(`/facilities/${facilityId}`),

  update: (facilityId: UUID, data: FacilityUpdate) =>
    httpClient.patch<FacilityResponse>(`/facilities/${facilityId}`, data),

  delete: (facilityId: UUID) =>
    httpClient.delete<void>(`/facilities/${facilityId}`),

  listByOrg: (orgId: UUID) =>
    httpClient.get<FacilityResponse[]>(`/facilities/${orgId}/list`),

  // Courts
  createCourt: (facilityId: UUID, data: CourtCreate) =>
    httpClient.post<CourtResponse>(`/facilities/${facilityId}/courts`, data),

  updateCourt: (courtId: UUID, data: CourtUpdate) =>
    httpClient.patch<CourtResponse>(`/facilities/courts/${courtId}`, data),

  deleteCourt: (courtId: UUID) =>
    httpClient.delete<void>(`/facilities/courts/${courtId}`),

  deactivateCourt: (courtId: UUID, reason?: string) =>
    httpClient.post<CourtDeactivationResponse>(
      `/facilities/courts/${courtId}/deactivate?reason=${encodeURIComponent(
        reason ?? "Court deactivated",
      )}`,
    ),

  listCourts: (facilityId: UUID) =>
    httpClient.get<CourtResponse[]>(`/facilities/${facilityId}/courts`),

  // Drop-in discovery — today's live + upcoming drop-in windows near the
  // player. Only orgs that are live + venue-approved with a matching open_play
  // pricing rule for today's facility-local weekday appear (Slice 1, unified
  // pricing). Optional lat/lng adds a distance_km + distance sort. The route
  // prefix is /facilities, so this lives on facilityApi (not dropinApi).
  getOpenPlayTonight: (latitude?: number, longitude?: number) => {
    const sp = new URLSearchParams();
    if (latitude !== undefined) sp.set("latitude", String(latitude));
    if (longitude !== undefined) sp.set("longitude", String(longitude));
    const qs = sp.toString();
    return httpClient.get<OpenPlayFacility[]>(
      `/facilities/open-play-tonight${qs ? `?${qs}` : ""}`,
    );
  },

  getOperatingHours: (facilityId: UUID) =>
    httpClient.get<FacilityOperatingHoursSnapshot>(
      `/facilities/${facilityId}/operating-hours`,
    ),

  previewOperatingHours: (
    facilityId: UUID,
    command: FacilityOperatingHoursChangeCommand,
  ) =>
    httpClient.post<FacilityOperatingHoursChangePreview>(
      `/facilities/${facilityId}/operating-hours/preview`,
      command,
    ),

  setOperatingHours: (
    facilityId: UUID,
    command: FacilityOperatingHoursChangeCommand,
  ) =>
    httpClient.put<FacilityOperatingHoursSnapshot>(
      `/facilities/${facilityId}/operating-hours`,
      command,
    ),

  getOperatingHoursProjection: (facilityId: UUID, localDate: string) =>
    httpClient.get<FacilityOperatingHoursDateProjection>(
      `/facilities/${facilityId}/operating-hours/projection?local_date=${encodeURIComponent(localDate)}`,
    ),

  getOperatingHoursProjections: (
    facilityId: UUID,
    startsOn: string,
    days: number,
  ) =>
    httpClient.get<FacilityOperatingHoursProjectionPage>(
      `/facilities/${facilityId}/operating-hours/projections?starts_on=${encodeURIComponent(startsOn)}&days=${encodeURIComponent(String(days))}`,
    ),

  // Pricing rules
  createPricingRule: (facilityId: UUID, data: PricingRuleCreate) =>
    httpClient.post<PricingRuleResponse>(
      `/facilities/${facilityId}/pricing-rules`,
      data,
    ),

  updatePricingRule: (ruleId: UUID, data: PricingRuleUpdate) =>
    httpClient.put<PricingRuleResponse>(
      `/facilities/pricing-rules/${ruleId}`,
      data,
    ),

  deletePricingRule: (ruleId: UUID) =>
    httpClient.delete<void>(`/facilities/pricing-rules/${ruleId}`),

  listPricingRules: (facilityId: UUID) =>
    httpClient.get<PricingRuleResponse[]>(
      `/facilities/${facilityId}/pricing-rules`,
    ),

  quoteReservation: (facilityId: UUID, data: ReservationQuoteRequest) =>
    httpClient.post<ReservationQuoteResponse>(
      `/facilities/${facilityId}/reservation-quote`,
      data,
    ),

  // Combined multi-slot price preview (#1312). Each slot reuses the SAME
  // reservation quote the booking would charge. Backend 409s on a
  // mixed-currency cart; surface that to the caller via the httpClient error.
  cartQuote: (facilityId: UUID, data: CartQuoteRequest) =>
    httpClient.post<CartQuoteResponse>(
      `/facilities/${facilityId}/cart-quote`,
      data,
    ),

  listPublicCampaigns: (facilityId: UUID) =>
    httpClient.get<FacilityCampaignPublicResponse[]>(
      `/facilities/${facilityId}/public/campaigns`,
    ),

  // Membership plans
  createMembershipPlan: (facilityId: UUID, data: MembershipPlanCreate) =>
    httpClient.post<MembershipPlanResponse>(
      `/facilities/${facilityId}/membership-plans`,
      data,
    ),

  updateMembershipPlan: (planId: UUID, data: MembershipPlanUpdate) =>
    httpClient.patch<MembershipPlanResponse>(
      `/facilities/membership-plans/${planId}`,
      data,
    ),

  listMembershipPlans: (facilityId: UUID) =>
    httpClient.get<MembershipPlanResponse[]>(
      `/facilities/${facilityId}/membership-plans`,
    ),

  // Price overrides
  createPriceOverride: (
    facilityId: UUID,
    data: MembershipPriceOverrideCreate,
  ) =>
    httpClient.post<MembershipPriceOverrideResponse>(
      `/facilities/${facilityId}/price-overrides`,
      data,
    ),

  // Sessions
  createSession: (facilityId: UUID, data: SessionCreate) =>
    httpClient.post<SessionResponse>(
      `/facilities/${facilityId}/sessions`,
      data,
    ),

  cancelSession: (facilityId: UUID, sessionId: UUID, courtId: UUID) =>
    httpClient.delete<void>(
      `/facilities/${facilityId}/sessions/${sessionId}/courts/${courtId}`,
    ),

  checkInPlayer: (courtId: UUID) =>
    httpClient.post<CheckInResponse>(`/facilities/courts/${courtId}/check-in`),

  // Coach/owner skill assessment — a band that maps to a Glicko rating and SETS
  // the player's PlayerRating; returns the resulting rating. evaluator_id +
  // facility_id are server-derived (token + path), not in `data`.
  submitSkillAssessment: (facilityId: UUID, data: SkillAssessmentCreate) =>
    httpClient.post<PlayerRatingResponse>(
      `/facilities/${facilityId}/skill-assessment`,
      data,
    ),

  // Cancellation policy
  setCancellationPolicy: (facilityId: UUID, data: CancellationPolicyCreate) =>
    httpClient.put<CancellationPolicyResponse>(
      `/facilities/${facilityId}/cancellation-policy`,
      data,
    ),

  getCancellationPolicy: (facilityId: UUID) =>
    httpClient.get<CancellationPolicyResponse>(
      `/facilities/${facilityId}/cancellation-policy`,
    ),

  // Family groups
  createFamilyGroup: (facilityId: UUID, data: FamilyGroupCreate) =>
    httpClient.post<FamilyGroupResponse>(
      `/facilities/${facilityId}/family-groups`,
      data,
    ),

  listFamilyGroups: (facilityId: UUID) =>
    httpClient.get<FamilyGroupResponse[]>(
      `/facilities/${facilityId}/family-groups`,
    ),

  getFamilyGroup: (groupId: UUID) =>
    httpClient.get<FamilyGroupResponse>(`/facilities/family-groups/${groupId}`),

  addFamilyMember: (groupId: UUID, memberUserId: string) =>
    httpClient.post<FamilyGroupMemberResponse>(
      `/facilities/family-groups/${groupId}/members`,
      {
        member_user_id: memberUserId,
      },
    ),

  removeFamilyMember: (groupId: UUID, memberUserId: string) =>
    httpClient.delete<void>(
      `/facilities/family-groups/${groupId}/members/${encodeURIComponent(memberUserId)}`,
    ),

  // Bookings
  getAvailability: (facilityId: UUID, date: string) =>
    httpClient.get<AvailabilitySlot[]>(
      `/facilities/${facilityId}/availability?date=${date}`,
    ),

  createBooking: (facilityId: UUID, data: BookingCreate) =>
    httpClient.post<BookingResponse>(
      `/facilities/${facilityId}/bookings`,
      data,
    ),

  // Book N cart slots under ONE Stripe charge (#1313). Waiver is checked once
  // up front (428); a conflicting slot or mixed-currency cart (409) rolls the
  // whole cart back and never leaves a charge. The idempotency_key in `data`
  // makes a retried request safe.
  batchCreateBooking: (facilityId: UUID, data: BatchBookingRequest) =>
    httpClient.post<BatchBookingResponse>(
      `/facilities/${facilityId}/bookings/batch`,
      data,
    ),

  listBookings: (facilityId: UUID) =>
    httpClient.get<BookingResponse[]>(`/facilities/${facilityId}/bookings`),

  getBooking: (bookingId: UUID) =>
    httpClient.get<BookingResponse>(`/facilities/bookings/${bookingId}`),

  createBookingCancellationQuote: (bookingId: UUID) =>
    httpClient.post<BookingCancellationQuoteResponse>(
      `/facilities/bookings/${encodeURIComponent(bookingId)}/cancellation-quotes`,
    ),

  cancelBooking: (bookingId: UUID, data: BookingCancelRequest) =>
    httpClient.post<BookingCancellationResponse>(
      `/facilities/bookings/${encodeURIComponent(bookingId)}/cancel`,
      data,
    ),

  recoverBookingCancellation: (bookingId: UUID) =>
    httpClient.post<BookingCancellationResponse>(
      `/facilities/bookings/${encodeURIComponent(bookingId)}/cancel/recover`,
    ),

  // Booking participants (host roster — NON-CHARGING). The host adds another
  // player onto their already-paid court; the body carries ONLY the guest's
  // user_id (the host is the token user, server-derived). 409 on a duplicate.
  addBookingParticipant: (
    facilityId: UUID,
    bookingId: UUID,
    data: BookingParticipantAdd,
  ) =>
    httpClient.post<BookingParticipantResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/participants`,
      data,
    ),

  listBookingParticipants: (facilityId: UUID, bookingId: UUID) =>
    httpClient.get<BookingParticipantResponse[]>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/participants`,
    ),

  // Remove a guest from the roster (204 → void). 409 if they have already
  // checked in. The target id is a Firebase uid → path-encoded.
  removeBookingParticipant: (
    facilityId: UUID,
    bookingId: UUID,
    userId: string,
  ) =>
    httpClient.delete<void>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/participants/${encodeURIComponent(userId)}`,
    ),

  // Booking invitations (invite another player to a court). The body carries
  // ONLY invitee_id (inviter = token user, server-derived).
  createBookingInvitation: (
    facilityId: UUID,
    bookingId: UUID,
    data: { invitee_id: string },
  ) =>
    httpClient.post<BookingInvitationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations`,
      data,
    ),

  // Invite a player to EVERY slot in the booking's recurring group in one call
  // (a single booking is a group-of-one server-side). The /batch suffix is
  // load-bearing — it must NOT collapse onto the single-slot .../invitations path.
  // Returns one response per slot actually invited ([] when all were already invited).
  createBookingInvitationsForGroup: (
    facilityId: UUID,
    bookingId: UUID,
    data: { invitee_id: string },
  ) =>
    httpClient.post<BookingInvitationResponse[]>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations/batch`,
      data,
    ),

  listBookingInvitations: (facilityId: UUID, bookingId: UUID) =>
    httpClient.get<BookingInvitationResponse[]>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations`,
    ),

  acceptBookingInvitation: (
    facilityId: UUID,
    bookingId: UUID,
    invitationId: UUID,
  ) =>
    httpClient.post<BookingInvitationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations/${encodeURIComponent(invitationId)}/accept`,
    ),

  declineBookingInvitation: (
    facilityId: UUID,
    bookingId: UUID,
    invitationId: UUID,
  ) =>
    httpClient.post<BookingInvitationResponse>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations/${encodeURIComponent(invitationId)}/decline`,
    ),

  // Cancel a pending booking invitation (host only).
  // DELETE /facilities/{facilityId}/bookings/{bookingId}/invitations/{invitationId} → 204.
  cancelBookingInvitation: (
    facilityId: UUID,
    bookingId: UUID,
    invitationId: UUID,
  ) =>
    httpClient.delete<void>(
      `/facilities/${encodeURIComponent(facilityId)}/bookings/${encodeURIComponent(bookingId)}/invitations/${encodeURIComponent(invitationId)}`,
    ),
};
